/**
 * Staff: Tenant onboarden — Wizard für die einstufige Backend-Foundation:
 * Keycloak-Realm + Service-Account-Client + Claim-Mapper, Cortex-DB Rows
 * (rate-config, tool-allowlist, org_settings, group->server map) und
 * CoreDNS-Override für Tenant-Service-Subdomains.
 *
 * Spec / Backend: it-sce-platform services/portal-api/internal/tenant_onboarding.
 * Idempotent: wiederholter Aufruf mit gleichem Slug überspringt vorhandenes.
 *
 * TQ #9696 (Staff-Migration Teil C, Bundle #9523). Portiert aus
 * customer-portal/src/pages/staff/StaffTenantOnboarding.tsx — verbatim
 * (useAuth().api → portal-api /api/staff/tenant-onboarding). Der Wizard
 * validiert den Slug clientseitig gegen die Zielbild-Naming-Konvention
 * (SLUG_RE = [a-z0-9-]{2,30}, RESERVED_SLUGS), die eigentliche Anlage +
 * Slug-Konsistenz-Check macht das Backend idempotent.
 */

import { useState, useMemo } from 'react'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'

type OnboardingModule = 'mail' | 'storage' | 'web' | 'erp' | 'aria'

interface OnboardingRequest {
  slug: string
  name: string
  modules: OnboardingModule[]
  mail_domain?: string
  nextcloud_name?: string
  tenant_domain?: string
}

type StepStatus = 'done' | 'skipped' | 'needs_attention' | 'failed'

interface StepResult {
  name: string
  status: StepStatus
  message?: string
  took: string
}

type OverallStatus = 'done' | 'partial' | 'failed'

interface OnboardingResponse {
  slug: string
  realm: string
  realm_url: string
  initial_owner_pwd?: string
  steps: StepResult[]
  overall_status: OverallStatus
  needs_attention?: string[]
  started_at: string
  finished_at: string
}

const ALL_MODULES: { id: OnboardingModule; label: string; hint: string }[] = [
  { id: 'mail', label: 'Mail (Stalwart)', hint: 'Postfach inkl. CalDAV/CardDAV. Setzt mail.<domain> DNS voraus.' },
  { id: 'storage', label: 'Storage (Nextcloud/OpenCloud)', hint: 'Cloud-Speicher, Sync, Sharing.' },
  { id: 'web', label: 'Web (WordPress)', hint: 'Kunden-Website inkl. Hosting.' },
  { id: 'erp', label: 'ERP (ERPNext)', hint: 'Multi-Tenant ERPNext auf K3s z6e.' },
  { id: 'aria', label: 'Aria (Cortex)', hint: 'Personalisierter AI-Assistent inkl. Tool-Allowlist + Soul-Addendum.' },
]

const SLUG_RE = /^[a-z0-9-]{2,30}$/
const RESERVED_SLUGS = ['itsce', 'platform', 'system', 'default', 'kube-system', 'kube-public']

interface FormErrors {
  slug?: string
  name?: string
  modules?: string
}

function validate(req: OnboardingRequest): FormErrors {
  const errors: FormErrors = {}
  if (!req.slug.trim()) errors.slug = 'Slug ist Pflicht.'
  else if (!SLUG_RE.test(req.slug)) errors.slug = 'Slug: kleinbuchstaben, ziffern, bindestrich, 2-30 zeichen.'
  else if (RESERVED_SLUGS.includes(req.slug)) errors.slug = `'${req.slug}' ist reserviert.`
  if (!req.name.trim()) errors.name = 'Name ist Pflicht.'
  if (req.modules.length === 0) errors.modules = 'Mindestens ein Modul wählen.'
  return errors
}

function statusIcon(status: StepStatus): string {
  switch (status) {
    case 'done': return '✅'
    case 'skipped': return '⏭️'
    case 'needs_attention': return '⚠️'
    case 'failed': return '❌'
  }
}

function statusLabel(status: StepStatus): string {
  switch (status) {
    case 'done': return 'Erledigt'
    case 'skipped': return 'Bereits vorhanden'
    case 'needs_attention': return 'Operator-Aktion'
    case 'failed': return 'Fehlgeschlagen'
  }
}

function StepRow({ step }: { step: StepResult }) {
  const colors: Record<StepStatus, string> = {
    done: '#10b981',
    skipped: '#6b7280',
    needs_attention: '#f59e0b',
    failed: '#ef4444',
  }
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '32px 1fr 130px 80px',
      gap: '12px',
      padding: '10px 12px',
      borderBottom: '1px solid #e5e7eb',
      alignItems: 'center',
    }}>
      <div style={{ fontSize: '18px' }}>{statusIcon(step.status)}</div>
      <div>
        <div style={{ fontWeight: 600, fontFamily: 'monospace' }}>{step.name}</div>
        {step.message && (
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px', whiteSpace: 'pre-wrap' }}>
            {step.message}
          </div>
        )}
      </div>
      <div style={{ color: colors[step.status], fontSize: '12px', fontWeight: 600 }}>
        {statusLabel(step.status)}
      </div>
      <div style={{ color: '#9ca3af', fontSize: '12px', textAlign: 'right' }}>{step.took}</div>
    </div>
  )
}

export default function StaffTenantOnboarding() {
  const { api } = useAuth()
  const [req, setReq] = useState<OnboardingRequest>({
    slug: '',
    name: '',
    modules: ['aria', 'mail'],
  })
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<OnboardingResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  const errors = useMemo(() => (busy ? {} : validate(req)), [req, busy])
  const hasErrors = Object.keys(errors).length > 0

  const toggleModule = (m: OnboardingModule) => {
    setReq(r => ({
      ...r,
      modules: r.modules.includes(m) ? r.modules.filter(x => x !== m) : [...r.modules, m],
    }))
  }

  const submit = async () => {
    if (hasErrors) return
    if (!api) {
      setError('API nicht verfügbar — bist du eingeloggt?')
      return
    }
    setBusy(true)
    setError(null)
    setResult(null)
    try {
      const payload: OnboardingRequest = {
        slug: req.slug.trim(),
        name: req.name.trim(),
        modules: req.modules,
      }
      if (req.mail_domain?.trim()) payload.mail_domain = req.mail_domain.trim()
      if (req.nextcloud_name?.trim()) payload.nextcloud_name = req.nextcloud_name.trim()
      if (req.tenant_domain?.trim()) payload.tenant_domain = req.tenant_domain.trim()

      const resp = await api.post<OnboardingResponse>(
        '/api/staff/tenant-onboarding',
        payload,
      )
      setResult(resp.data)
    } catch (e: any) {
      const apiErr = e?.response?.data?.error || e?.response?.data?.reason
      setError(apiErr || e?.message || 'Unbekannter Fehler')
    } finally {
      setBusy(false)
    }
  }

  const resetForm = () => {
    setResult(null)
    setError(null)
    setReq({ slug: '', name: '', modules: ['aria', 'mail'] })
  }

  return (
    <div style={{ maxWidth: '960px' }}>
      <div style={{ marginBottom: '20px' }}>
        <p style={{ color: '#6b7280', fontSize: '14px', lineHeight: '1.5', margin: 0 }}>
          Legt Keycloak-Realm, Service-Account-Client, Cortex-DB-Rows
          (rate-config, tool-allowlist, org_settings, group→server map)
          und CoreDNS-Templates für Tenant-Service-Subdomains in einem
          Schritt an. Idempotent — erneuter Aufruf mit gleichem Slug
          überspringt bereits vorhandene Einträge.
        </p>
      </div>

      {!result && (
        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px', fontSize: '14px' }}>
                Slug *
              </label>
              <input
                type="text"
                value={req.slug}
                onChange={e => setReq(r => ({ ...r, slug: e.target.value.toLowerCase() }))}
                placeholder="z.B. glasbrenner"
                disabled={busy}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: errors.slug ? '1px solid #ef4444' : '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                }}
              />
              {errors.slug && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>{errors.slug}</div>}
              <div style={{ color: '#9ca3af', fontSize: '12px', marginTop: '4px' }}>
                = Keycloak-Realm-Name = Cortex-Tenant-ID. <code>[a-z0-9-]{`{2,30}`}</code>
              </div>
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px', fontSize: '14px' }}>
                Anzeige-Name *
              </label>
              <input
                type="text"
                value={req.name}
                onChange={e => setReq(r => ({ ...r, name: e.target.value }))}
                placeholder="z.B. Glasbrenner Baggerbetrieb"
                disabled={busy}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: errors.name ? '1px solid #ef4444' : '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                }}
              />
              {errors.name && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>{errors.name}</div>}
            </div>
          </div>

          <div style={{ marginTop: '20px' }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', fontSize: '14px' }}>
              Gebuchte Module *
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {ALL_MODULES.map(m => {
                const checked = req.modules.includes(m.id)
                return (
                  <label
                    key={m.id}
                    style={{
                      display: 'flex',
                      gap: '10px',
                      padding: '10px 12px',
                      border: `1px solid ${checked ? '#FF6B35' : '#e5e7eb'}`,
                      borderRadius: '6px',
                      background: checked ? '#fff7ed' : 'white',
                      cursor: busy ? 'not-allowed' : 'pointer',
                      alignItems: 'flex-start',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleModule(m.id)}
                      disabled={busy}
                      style={{ marginTop: '3px' }}
                    />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '14px' }}>{m.label}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{m.hint}</div>
                    </div>
                  </label>
                )
              })}
            </div>
            {errors.modules && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px' }}>{errors.modules}</div>}
          </div>

          <div style={{
            marginTop: '20px',
            padding: '16px',
            background: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
          }}>
            <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '10px', color: '#374151' }}>
              Optionale Felder
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: '#6b7280' }}>
                  Mail-Domain
                </label>
                <input
                  type="text"
                  value={req.mail_domain || ''}
                  onChange={e => setReq(r => ({ ...r, mail_domain: e.target.value }))}
                  placeholder="example.de"
                  disabled={busy}
                  style={{
                    width: '100%',
                    padding: '6px 10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '13px',
                    fontFamily: 'monospace',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: '#6b7280' }}>
                  Nextcloud-Name
                </label>
                <input
                  type="text"
                  value={req.nextcloud_name || ''}
                  onChange={e => setReq(r => ({ ...r, nextcloud_name: e.target.value }))}
                  placeholder="nextcloud-{`{slug}`}"
                  disabled={busy}
                  style={{
                    width: '100%',
                    padding: '6px 10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '13px',
                    fontFamily: 'monospace',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: '#6b7280' }}>
                  Tenant-Domain
                </label>
                <input
                  type="text"
                  value={req.tenant_domain || ''}
                  onChange={e => setReq(r => ({ ...r, tenant_domain: e.target.value }))}
                  placeholder="kunden-domain.de"
                  disabled={busy}
                  style={{
                    width: '100%',
                    padding: '6px 10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '13px',
                    fontFamily: 'monospace',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px', lineHeight: '1.4' }}>
              Tenant-Domain triggert CoreDNS-Template-Override (siehe <code>.claude/rules/dns-tenant-domains.md</code>).
              Externer DNS bleibt unangetastet — interner Pod-Resolve via CoreDNS.
            </div>
          </div>

          {error && (
            <div style={{
              marginTop: '16px',
              padding: '12px 16px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              color: '#991b1b',
              fontSize: '13px',
            }}>
              <strong>Fehler:</strong> {error}
            </div>
          )}

          <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={submit}
              disabled={busy || hasErrors}
              style={{
                padding: '10px 24px',
                background: hasErrors || busy ? '#9ca3af' : '#FF6B35',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontWeight: 600,
                fontSize: '14px',
                cursor: busy || hasErrors ? 'not-allowed' : 'pointer',
              }}
            >
              {busy ? 'Onboarding läuft…' : 'Tenant anlegen'}
            </button>
          </div>
        </div>
      )}

      {result && (
        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          marginBottom: '24px',
        }}>
          <div style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>Tenant</div>
              <div style={{ fontSize: '18px', fontWeight: 600 }}>
                {result.slug} {result.realm_url && (
                  <a
                    href={result.realm_url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: '13px', color: '#FF6B35', marginLeft: '12px' }}
                  >
                    KC-Realm ↗
                  </a>
                )}
              </div>
            </div>
            <div style={{
              padding: '6px 12px',
              borderRadius: '4px',
              fontSize: '13px',
              fontWeight: 600,
              background: result.overall_status === 'done'
                ? '#d1fae5'
                : result.overall_status === 'partial' ? '#fef3c7' : '#fee2e2',
              color: result.overall_status === 'done'
                ? '#065f46'
                : result.overall_status === 'partial' ? '#92400e' : '#991b1b',
            }}>
              {result.overall_status.toUpperCase()}
            </div>
          </div>

          <div>
            {result.steps.map((step, i) => <StepRow key={i} step={step} />)}
          </div>

          {result.needs_attention && result.needs_attention.length > 0 && (
            <div style={{
              padding: '16px 20px',
              background: '#fffbeb',
              borderTop: '1px solid #fde68a',
            }}>
              <div style={{ fontWeight: 600, fontSize: '14px', color: '#92400e', marginBottom: '8px' }}>
                Nächste Schritte (Operator-Aktion erforderlich)
              </div>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#78350f', lineHeight: '1.7' }}>
                {result.needs_attention.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            </div>
          )}

          <div style={{
            padding: '16px 20px',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '12px',
            background: '#f9fafb',
          }}>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              {new Date(result.started_at).toLocaleString('de-DE')} → {new Date(result.finished_at).toLocaleString('de-DE')}
            </div>
            <button
              onClick={resetForm}
              style={{
                padding: '6px 16px',
                background: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              Weiteren Tenant anlegen
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
